<div
  class="audit-trail-mini flex flex-column surface-ground border-1 surface-border border-round"
  [style.height]="height()"
>
  <!-- Header -->
  <div
    class="flex align-items-center justify-content-between p-3 border-bottom-1 surface-border"
  >
    <div class="flex align-items-center gap-2">
      <i class="pi pi-history text-primary text-xl"></i>
      <div class="flex flex-column">
        <h3 class="m-0 text-900 font-semibold text-base">{{ title() }}</h3>
        <p class="m-0 text-600 text-xs">
          {{ 'AUDIT_TRAIL.RECENT_ACTIVITY_FOR' | translate }} {{ entityType().replace('_', ' ') | titlecase }}
        </p>
      </div>
    </div>
    <div class="flex align-items-center gap-1">
      <p-button
        icon="pi pi-refresh"
        severity="secondary"
        [text]="true"
        size="small"
        (onClick)="onRefresh()"
        [loading]="loading$ | async"
        aria-label="refresh-audit-mini"
        [pTooltip]="'AUDIT_TRAIL.REFRESH' | translate"
        tooltipPosition="top"
      />
      @if (showCloseButton()) {
        <p-button
          icon="pi pi-times"
          severity="secondary"
          [text]="true"
          size="small"
          (onClick)="closeComponent()"
          aria-label="close-audit-mini"
          [pTooltip]="'AUDIT_TRAIL.CLOSE' | translate"
          tooltipPosition="top"
        />
      }
    </div>
  </div>

  <!-- Stats Cards (if enabled) -->
  @if (showStats()) {
    <div class="p-3 border-bottom-1 surface-border">
      <div class="grid">
        <div class="col-3">
          <div class="text-center">
            <div
              class="flex align-items-center justify-content-center gap-2 mb-1"
            >
              <i class="pi pi-chart-bar text-primary text-sm"></i>
              <div class="text-xl font-bold text-900">{{ totalEvents }}</div>
            </div>
            <div class="text-xs text-600 uppercase">{{ 'AUDIT_TRAIL.TOTAL_EVENTS' | translate }}</div>
          </div>
        </div>
        <div class="col-3">
          <div class="text-center border-left-1 surface-border">
            <div
              class="flex align-items-center justify-content-center gap-2 mb-1"
            >
              <i class="pi pi-pencil text-blue-500 text-sm"></i>
              <div class="text-xl font-bold text-primary">
                {{ totalUpdates }}
              </div>
            </div>
            <div class="text-xs text-600 uppercase">{{ 'AUDIT_TRAIL.UPDATES' | translate }}</div>
          </div>
        </div>
        <div class="col-3">
          <div class="text-center border-left-1 surface-border">
            <div
              class="flex align-items-center justify-content-center gap-2 mb-1"
            >
              <i class="pi pi-plus-circle text-green-500 text-sm"></i>
              <div class="text-xl font-bold text-green-500">
                {{ totalCreates }}
              </div>
            </div>
            <div class="text-xs text-600 uppercase">{{ 'AUDIT_TRAIL.CREATED' | translate }}</div>
          </div>
        </div>
        <div class="col-3">
          <div class="text-center border-left-1 surface-border">
            <div
              class="flex align-items-center justify-content-center gap-2 mb-1"
            >
              <i class="pi pi-trash text-red-500 text-sm"></i>
              <div class="text-xl font-bold text-red-500">
                {{ totalDeletes }}
              </div>
            </div>
            <div class="text-xs text-600 uppercase">{{ 'AUDIT_TRAIL.DELETED' | translate }}</div>
          </div>
        </div>
      </div>
    </div>
  }

  <!-- Filters (if enabled) -->
  @if (showFilters()) {
    <div class="p-3 surface-50 border-bottom-1 surface-border">
      <div class="flex align-items-center justify-content-between mb-2">
        <div class="flex align-items-center gap-2">
          <i class="pi pi-filter text-600 text-sm"></i>
          <span class="text-sm font-medium text-700">{{ 'AUDIT_TRAIL.FILTER' | translate }}</span>
        </div>
        <div class="flex align-items-center gap-2">
          @if (hasActiveFilters) {
            <p-button
              [label]="'AUDIT_TRAIL.CLEAR' | translate"
              icon="pi pi-times"
              severity="secondary"
              [text]="true"
              size="small"
              (onClick)="clearFilters()"
              aria-label="clear-filters"
              [pTooltip]="'AUDIT_TRAIL.CLEAR_FILTER' | translate"
              tooltipPosition="top"
            />
          }
        </div>
      </div>
      <div class="w-full">
        <p-select
          [options]="operationOptions"
          [(ngModel)]="selectedOperation"
          (ngModelChange)="onOperationChange()"
          optionLabel="label"
          optionValue="value"
          [placeholder]="'AUDIT_TRAIL.ALL_OPERATIONS' | translate"
          aria-label="operation-filter"
          styleClass="w-full p-inputtext-sm"
        >
          <ng-template pTemplate="selectedItem" let-selectedOption>
            @if (selectedOption) {
              <div class="flex align-items-center gap-2">
                <i [class]="selectedOption.icon + ' text-sm'"></i>
                <span class="text-sm">{{ selectedOption.label }}</span>
              </div>
            }
          </ng-template>
          <ng-template pTemplate="item" let-option>
            <div class="flex align-items-center gap-2">
              <i [class]="option.icon + ' text-sm'"></i>
              <span class="text-sm">{{ option.label }}</span>
            </div>
          </ng-template>
        </p-select>
      </div>
    </div>
  }

  <!-- Events List -->
  <div class="flex-1 overflow-auto" style="min-height: 0">
    @if (loading$ | async) {
      <div class="flex flex-column gap-2 p-3">
        @for (i of [1, 2, 3]; track i) {
          <div
            class="flex align-items-start gap-3 p-2 border-1 surface-border border-round w-full"
          >
            <p-skeleton shape="circle" size="1.5rem" />
            <div class="flex-1 min-w-0 w-full">
              <div class="flex flex-wrap align-items-center gap-2 mb-1">
                <p-skeleton width="30%" height="0.875rem" />
                <p-skeleton width="15%" height="0.875rem" />
                <p-skeleton width="20%" height="0.875rem" />
              </div>
              <div class="flex flex-wrap align-items-center gap-2">
                <p-skeleton width="20%" height="0.75rem" />
                <p-skeleton width="25%" height="0.75rem" />
              </div>
            </div>
          </div>
        }
      </div>
    } @else {
      <!-- Event Grid -->
      @if ((currentEvents$ | async) && (currentEvents$ | async)!.length > 0) {
        <div class="flex flex-column gap-1 p-3">
          @for (
            event of currentEvents$ | async;
            track trackByEventId($index, event)
          ) {
            <div
              class="flex align-items-start gap-3 p-2 border-1 surface-border border-round w-full hover:surface-50 transition-all transition-duration-200 cursor-pointer"
            >
              <!-- Operation Icon -->
              <div class="flex-shrink-0">
                <div
                  class="flex align-items-center justify-content-center border-round flex-shrink-0 w-2rem h-2rem mt-1"
                  [style.backgroundColor]="
                    getOperationConfig(event.operation).bgColor
                  "
                  [style.color]="getOperationConfig(event.operation).color"
                >
                  <i
                    [class]="
                      getOperationConfig(event.operation).icon + ' text-xs'
                    "
                  ></i>
                </div>
              </div>
              <!-- Event Content -->
              <div class="flex-1 min-w-0">
                <!-- Main Statement -->
                <div class="text-sm line-height-3 mb-1">
                  <staff-info
                    [staffId]="event.staff_id"
                    [dataProperty]="'id'"
                    styleClass="font-semibold text-900 text-sm"
                  />
                  <span class="text-700 mx-1">{{
                    generateEventStatement(event).action
                  }}</span>
                  <!-- Field display (hide for client creation with all-fields) -->
                  @if (
                    generateEventStatement(event).field &&
                    !shouldHideFieldDisplay(event)
                  ) {
                    <p-chip
                      [label]="generateEventStatement(event).field || ''"
                      styleClass="mx-1 text-xs p-chip-sm"
                    />
                    <span class="text-700 mx-1">{{ 'AUDIT_TRAIL.OF' | translate }}</span>
                  }
                  <!-- Client Creation Special Display -->
                  @if (isClientCreateEvent(event)) {
                    <span
                      class="font-medium text-primary cursor-pointer hover:text-primary-600 underline"
                      tooltipPosition="top"
                      aria-label="client-profile-link-mini"
                    >
                      {{ getClientDisplayName(event) }}
                    </span>
                  } @else {
                    <span class="font-medium text-900">{{
                      event.entity_primary_name || event.entity_id
                    }}</span>
                  }
                  <!-- Regular Entity Name Display -->
                  @if (generateEventStatement(event).hasValueChange) {
                    <span class="text-700">
                      @if (generateEventStatement(event).preposition) {
                        <span class="mx-1">{{
                          generateEventStatement(event).preposition
                        }}</span>
                      }
                      <!-- Show staff info if field matches staff patterns -->
                      @if (
                        isStaffField(event.fields) && getStaffIdFromEvent(event)
                      ) {
                        @if (
                          generateEventStatement(event).showOldValue &&
                          event.old_value &&
                          getNumberValue(event.old_value)
                        ) {
                          <staff-info
                            [staffId]="getNumberValue(event.old_value)!"
                            [dataProperty]="'id'"
                            styleClass="mx-1 text-xs bg-red-100 text-red-800 border-red-200 px-2 py-1 border-round"
                          />
                        }
                        @if (
                          generateEventStatement(event).showOldValue &&
                          generateEventStatement(event).showNewValue
                        ) {
                          <i
                            class="pi pi-arrow-right text-400 mx-1 text-xs"
                          ></i>
                        }
                        @if (
                          generateEventStatement(event).showNewValue &&
                          event.new_value &&
                          getNumberValue(event.new_value)
                        ) {
                          <staff-info
                            [staffId]="getNumberValue(event.new_value)!"
                            [dataProperty]="'id'"
                            styleClass="mx-1 text-xs bg-green-100 text-green-800 border-green-200 px-2 py-1 border-round"
                          />
                        }
                      } @else {
                        <!-- Show enum status values for appointment/class status -->
                        @if (shouldShowEnumStatus(event)) {
                          @if (
                            generateEventStatement(event).showOldValue &&
                            event.old_value
                          ) {
                            <p-chip
                              [label]="
                                getEnumStatusLabel(event, event.old_value)
                              "
                              [styleClass]="
                                'mx-1 text-xs p-chip-sm ' +
                                getStatusStyleClass(event, event.old_value)
                              "
                            >
                              <i
                                [class]="
                                  getStatusIcon(event, event.old_value) +
                                  ' mr-1'
                                "
                              ></i>
                            </p-chip>
                          }
                          @if (
                            generateEventStatement(event).showOldValue &&
                            generateEventStatement(event).showNewValue
                          ) {
                            <i
                              class="pi pi-arrow-right text-400 mx-1 text-xs"
                            ></i>
                          }
                          @if (
                            generateEventStatement(event).showNewValue &&
                            event.new_value
                          ) {
                            <p-chip
                              [label]="
                                getEnumStatusLabel(event, event.new_value)
                              "
                              [styleClass]="
                                'mx-1 text-xs p-chip-sm ' +
                                getStatusStyleClass(event, event.new_value)
                              "
                            >
                              <i
                                [class]="
                                  getStatusIcon(event, event.new_value) +
                                  ' mr-1'
                                "
                              ></i>
                            </p-chip>
                          }
                        } @else {
                          @if (
                            generateEventStatement(event).showOldValue &&
                            event.old_value
                          ) {
                            <p-chip
                              [label]="event.old_value"
                              styleClass="mx-1 text-xs p-chip-sm"
                              severity="danger"
                            />
                          }
                          @if (
                            generateEventStatement(event).showOldValue &&
                            generateEventStatement(event).showNewValue
                          ) {
                            <i
                              class="pi pi-arrow-right text-400 mx-1 text-xs"
                            ></i>
                          }
                          @if (
                            generateEventStatement(event).showNewValue &&
                            event.new_value
                          ) {
                            <p-chip
                              [label]="event.new_value"
                              styleClass="mx-1 text-xs p-chip-sm"
                              severity="success"
                            />
                          }
                        }
                        <!-- Normal chip display -->
                      }
                      <!-- Regular chip display for non-staff fields -->
                    </span>
                  }
                </div>
                <!-- Secondary Information -->
                <div class="flex align-items-center gap-2 text-xs text-500">
                  <div class="flex align-items-center gap-1">
                    <i class="pi pi-clock text-xs"></i>
                    <span class="font-medium text-700">{{
                      event.timestamp | DayAgo
                    }}</span>
                  </div>
                  <span>•</span>
                  <div class="flex align-items-center gap-1">
                    <i class="pi pi-desktop text-xs"></i>
                    <span>{{ getAppSourceLabel(event.track_mode) }}</span>
                  </div>
                  @if (event.tenant_id) {
                    <span>•</span>
                  }
                  @if (event.tenant_id) {
                    <div class="flex align-items-center gap-1">
                      <i class="pi pi-building text-xs"></i>
                      <span class="font-medium">{{
                        getTenantName(event.tenant_id)
                      }}</span>
                    </div>
                  }
                </div>
              </div>
            </div>
          }
        </div>
      }
      <!-- Empty State -->
      @if ((currentEvents$ | async)?.length === 0) {
        <div class="text-center p-6 text-600">
          <i class="pi pi-exclamation-triangle text-300 text-4xl mb-3"></i>
          <h4 class="text-900 font-medium mb-2 text-sm">{{ 'AUDIT_TRAIL.NO_EVENTS_FOUND' | translate }}</h4>
          <p class="text-xs text-500 m-0 mb-3">
            {{ 'AUDIT_TRAIL.NO_ACTIVITY_RECORDED' | translate }}
            {{ entityType().replace('_', ' ') | lowercase }}
          </p>
          @if (hasActiveFilters) {
            <p-button
              [label]="'AUDIT_TRAIL.CLEAR_FILTER' | translate"
              severity="secondary"
              [outlined]="true"
              size="small"
              (onClick)="clearFilters()"
              aria-label="clear-filter-empty-state"
            />
          }
        </div>
      }
    }
  </div>

  <!-- Footer -->
  @if (showPagination() && totalRecords > 0) {
    <div class="border-top-1 surface-border">
      <p-paginator
        [rows]="pageSize"
        [totalRecords]="totalRecords"
        [first]="first"
        [rowsPerPageOptions]="[5, 10, 15, 20]"
        (onPageChange)="onPageChange($event)"
        [showCurrentPageReport]="true"
        currentPageReportTemplate="{first} to {last} of {totalRecords}"
        [showFirstLastIcon]="false"
        [showPageLinks]="false"
        [showJumpToPageDropdown]="false"
        styleClass="p-paginator-sm"
      />
    </div>
  }

  @if (!showPagination() || totalRecords === 0) {
    <div class="p-2 border-top-1 surface-border text-center">
      <div class="flex align-items-center justify-content-center gap-2">
        <i class="pi pi-info-circle text-xs text-600"></i>
        <p class="text-xs text-600 m-0">
          {{ 'AUDIT_TRAIL.SHOWING_RECENT_EVENTS' | translate:{count: (currentEvents$ | async)?.length || 0} }}
        </p>
      </div>
    </div>
  }
</div>
